<script lang="ts" setup>
import CurrentLanguage from '@/components/CurrentLanguage.vue';
import MediaField from '@/components/media/MediaField.vue';
import MediaItem from '@/components/media/MediaItem.vue';
import { Media, defaultMedia } from '@/models/media.model';
import { useEvents, Event } from '@/services/events.composable';
import mediaService from '@/services/media.service';
import { Actions, Mutations, ShiftCMSState } from '@/store';
import { NEW_ID } from '@/util/constants';
import { computed, onBeforeUnmount, onMounted, PropType, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';

const props = defineProps({
  data: {
    type: Object as PropType<{ media?: Media }>,
    required: true,
  },
});

const store = useStore<ShiftCMSState>();
const events = useEvents();
const route = useRoute();

const { uuid } = route.params; // for external editors
const { t } = useI18n();

const mediaSearch = ref<Media[]>([]);

const media = ref<Media>({ ...defaultMedia }); // upload media
const media2 = ref<Media>({ ...(props.data.media || defaultMedia) }); // edit media

const title = computed<string>(() =>
  media2.value.id === NEW_ID ? t('media.add') : t('media.edit'),
);
const action = computed<string>(() =>
  media2.value.id === NEW_ID ? t('media.add') : t('media.save'),
);
const tab = ref<string>('upload');
const search = ref<string>('');
const uploadValid = ref<boolean>(false);
const editValid = ref<boolean>(false);

const uploadMedia = async () => {
  if (uploadValid.value) {
    const result = await store.dispatch(Actions.UploadMedia, {
      uuid,
      media: media.value,
    });

    if (result) {
      events.emit(Event.UseMedia, result);
      store.dispatch(Actions.ShowNotification, {
        message: 'Uploaded media',
        type: 'success',
      });
    } else {
      store.dispatch(Actions.ShowNotification, {
        message: 'Failed to upload media',
        type: 'error',
      });
    }
  }
};
const addMedia = async () => {
  if (editValid.value) {
    if (media2.value.id !== NEW_ID) {
      const result = await store.dispatch(Actions.UpdateMedia, {
        uuid,
        media: media2.value,
      });

      if (result) {
        store.dispatch(Actions.ShowNotification, {
          message: 'Saved media',
          type: 'success',
        });
        events.emit(Event.UpdateMedia, result);
      } else {
        store.dispatch(Actions.ShowNotification, {
          message: 'Failed to save media',
          type: 'error',
        });
      }
    } else {
      const result = await store.dispatch(Actions.AddMedia, {
        uuid,
        media: media2.value,
      });

      if (result) {
        store.dispatch(Actions.ShowNotification, {
          message: 'Saved media',
          type: 'success',
        });
        events.emit(Event.UseMedia, result);
      } else {
        store.dispatch(Actions.ShowNotification, {
          message: 'Failed to add media',
          type: 'error',
        });
      }
    }
  }
};

const searchMedia = async () => {
  if (search.value) {
    const result = await store.dispatch(Actions.SearchMedia, search.value);

    mediaSearch.value = result.data.reduce((acc: Media[], v: Media) => {
      if (acc.map((a) => a.file?.uuid).includes(v.file?.uuid)) {
        return acc;
      }
      return [...acc, v];
    }, []);
  } else {
    store.dispatch(Actions.ShowNotification, {
      message: 'Please provide a value for searching',
      type: 'info',
    });
  }
};

const useMedia = (m: Media) => {
  Object.assign(media2.value, {
    ...defaultMedia,
    description: m.description,
    file: m.file,
  });

  editMedia(media2.value);
};
const editMedia = (m: Media) => {
  Object.assign(media2.value, m);
  tab.value = 'edit';
};
const cancelEdit = () => {
  store.commit(Mutations.HideSubDrawer);
  store.commit(Mutations.HideDrawer);
};

const updateLanguage = async () => {
  if (media2.value.id === NEW_ID) {
    return;
  }
  const result = await mediaService.get(media2.value.id);

  if (result.status) {
    Object.assign(media2.value, { ...result.data });
  }
};

onMounted(() => {
  events.on(Event.LanguageChanged, updateLanguage);
  if (props.data && props.data.media) {
    editMedia(props.data.media);
  } else {
    Object.assign(media.value, { ...defaultMedia });
    Object.assign(media2.value, { ...defaultMedia });
  }
  updateLanguage();
});

onBeforeUnmount(() => {
  events.off(Event.LanguageChanged, updateLanguage);
});
</script>
<template>
  <v-alert color="info" class="ma-4 mt-8 pa-4" v-if="media2.id === NEW_ID">
    {{ $t('media.uploadOrSearch') }}
  </v-alert>
  <v-tabs v-model="tab" fixed-tabs>
    <v-tab value="upload" v-if="media2.id === NEW_ID">{{
      $t('media.upload')
    }}</v-tab>
    <v-tab value="edit" v-if="media2.id !== NEW_ID">{{
      $t('media.edit')
    }}</v-tab>
    <v-tab value="search" v-if="media2.id === NEW_ID">{{
      $t('media.search')
    }}</v-tab>
  </v-tabs>
  <v-tabs-window v-model="tab">
    <v-tabs-window-item value="upload">
      <v-form v-model="uploadValid" @submit.prevent="uploadMedia">
        <v-container>
          <h2>{{ $t('media.uploadNew') }} <CurrentLanguage /></h2>
          <MediaField v-model="media" />
          <v-row>
            <v-col>
              <v-file-input
                label="File input"
                v-model="media.file"
                required
              ></v-file-input>
            </v-col>
          </v-row>
          <v-row>
            <v-col col="2"></v-col>

            <v-col>
              <v-btn class="mt-2" type="submit" color="success" block>{{
                $t('media.upload')
              }}</v-btn>
            </v-col>
            <v-col col="2"></v-col>
          </v-row>
        </v-container>
      </v-form>
    </v-tabs-window-item>
    <v-tabs-window-item value="edit">
      <v-container>
        <h2>{{ title }} <CurrentLanguage buttons /></h2>
        <v-form v-model="editValid" @submit.prevent="addMedia">
          <v-container>
            <MediaField v-model="media2" />
            <MediaItem v-if="media2.file" :media="media2" :showOptions="[]" />
            <v-row>
              <v-col col="2"></v-col>

              <v-col>
                <v-btn
                  class="mt-2"
                  type="button"
                  @click.prevent="cancelEdit"
                  block
                  >{{ t('media.cancel') }}</v-btn
                >
              </v-col>
              <v-col>
                <v-btn class="mt-2" type="submit" block>{{ action }}</v-btn>
              </v-col>
              <v-col col="2"></v-col>
            </v-row>
          </v-container>
        </v-form>
      </v-container>
    </v-tabs-window-item>
    <v-tabs-window-item value="search">
      <v-container>
        <h2>{{ $t('media.search') }}</h2>
        <v-row>
          <v-col cols="10">
            <v-text-field v-model="search" label="Search"> </v-text-field>
          </v-col>
          <v-col cols="2" class="mt-2">
            <v-btn @click="searchMedia">{{ $t('media.search') }}</v-btn>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <div class="images">
              <MediaItem
                :media="m"
                v-for="m in mediaSearch"
                :key="m.id"
                @use="useMedia"
              />
            </div>
          </v-col>
        </v-row>
      </v-container>
    </v-tabs-window-item>
  </v-tabs-window>
</template>
<style scoped>
.images {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: row;
  flex-wrap: wrap;
}
.images > * {
  width: calc(50% - 1em);
  margin: 0.25em;
}
</style>
